/**
 * "A new version is available" detector.
 *
 * The app is a single-page bundle: a tab left open keeps running the
 * JavaScript it loaded, for days if nobody reloads — so colleagues can
 * be looking at a screen that no longer exists (the printed handover
 * sheet after v2 went live, for instance) while the guide next to them
 * describes the new flow. Netlify serves index.html uncached, so we
 * simply re-fetch it now and then and compare the hashed bundle name
 * with the one we are running. On a mismatch the App shows a banner
 * with a Reload button; nothing is forced.
 */
import { signal } from "@preact/signals";

export const updateAvailable = signal(false);

const BUNDLE_RE = /index-[\w-]+\.js/;

function currentBundle(): string | null {
  const script = document.querySelector<HTMLScriptElement>('script[type="module"][src]');
  return script?.src.match(BUNDLE_RE)?.[0] ?? null;
}

async function check(): Promise<void> {
  const cur = currentBundle();
  if (!cur || updateAvailable.value) return;
  try {
    const r = await fetch(`/?v=${Date.now()}`, { cache: "no-store", headers: { Accept: "text/html" } });
    if (!r.ok) return;
    const live = (await r.text()).match(BUNDLE_RE)?.[0];
    if (live && live !== cur) updateAvailable.value = true;
  } catch {
    // offline or blocked — try again next time
  }
}

/** Start polling: first look after a minute, then every `intervalMs`,
 *  plus whenever the tab comes back to the foreground. No-op in dev
 *  (the dev server has no hashed bundle). */
export function startVersionCheck(intervalMs = 10 * 60_000): void {
  if (import.meta.env.DEV || !currentBundle()) return;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void check();
  });
  setTimeout(() => void check(), 60_000);
  setInterval(() => void check(), intervalMs);
}
